<!--
  InstructorAddModal — 기관에 강사 등록 (강사 동의 코드 절차, 2026-09-23)
  1) GET  /voucher/org/{org}/instructors/search?email=   — 정확 일치. name 은 가려서 옴(김**). is_instructor / already_member / pending_invite
  2) POST /voucher/org/{org}/instructors/invites {email}  — 강사 메일로 6자리 동의 코드 발송(24시간). 대기 중이면 재발송(resent=true, 요청당 3회·60초 간격)
  3) POST /voucher/org/{org}/instructors/invites/{invite_idx}/confirm {code} — 맞으면 소속 생성(201, 실명)
  종전 직접 등록 POST /instructors {users_idx} 는 409 ORG_MEMBER_CONSENT_REQUIRED — 더 쓰지 않는다
-->
<template>
  <a-modal :open="true" title="강사 등록" sub="강사가 동의해야 소속이 생깁니다 — 이메일로 찾아 동의 코드를 보내고, 강사가 전달한 코드를 입력하세요" size="md" @close="$emit('close')">
    <div class="p-form">
      <!-- 1. 검색 -->
      <div class="p-field">
        <label class="p-field__label">이메일 <span class="p-field__req">*</span></label>
        <div class="p-inline">
          <input
            v-model.trim="email"
            type="email"
            class="p-input"
            placeholder="instructor@example.com"
            autocomplete="off"
            :disabled="!!invite"
            @keyup.enter="search"
          />
          <a-btn v-if="!invite" kind="primary" :disabled="!email || searching" @click="search">{{ searching ? '검색 중...' : '검색' }}</a-btn>
          <a-btn v-else kind="ghost" @click="reset">다른 이메일</a-btn>
        </div>
        <div class="p-field__hint">부분 검색은 되지 않습니다. 가입 이메일 전체를 정확히 입력하세요.</div>
      </div>

      <div v-if="searchError" class="p-alert p-alert--err"><div class="p-alert__body">{{ searchError }}</div></div>
      <div v-else-if="notFound" class="p-alert p-alert--warn">
        <div class="p-alert__title">계정을 찾을 수 없습니다</div>
        <div class="p-alert__body">입력한 이메일로 가입한 활성 계정이 없습니다. 이메일을 다시 확인해 주세요.</div>
      </div>

      <!-- 검색 결과 -->
      <div v-if="result" class="p-result">
        <div class="p-cell-class">
          <div class="p-avatar">{{ (displayName || '?').charAt(0) }}</div>
          <div>
            <div class="p-cell-title">{{ displayName || '-' }} <span v-if="!invite || invite.status !== 'CONFIRMED'" class="p-cell-sub">(동의 전에는 이름이 가려집니다)</span></div>
            <div class="p-cell-sub p-mono">{{ result.email || email }}</div>
          </div>
        </div>

        <div v-if="result.is_instructor === false" class="p-alert p-alert--err p-mt-8">
          <div class="p-alert__title">강사 권한 승인을 먼저 받아야 합니다</div>
          <div class="p-alert__body">이 계정은 아직 승인된 강사가 아닙니다.</div>
        </div>
        <div v-else-if="result.already_member" class="p-alert p-alert--info p-mt-8">
          <div class="p-alert__title">이미 등록된 강사</div>
          <div class="p-alert__body">이 계정은 이미 이 기관의 강사입니다.</div>
        </div>

        <!-- 2. 코드 발송 / 3. 코드 확인 -->
        <template v-else>
          <div v-if="!invite" class="p-alert p-alert--ok p-mt-8">
            <div class="p-alert__body">등록할 수 있는 계정입니다. ‘동의 코드 보내기’ 를 누르면 강사 메일로 6자리 코드가 갑니다(24시간 유효). 강사가 동의하면 코드를 전달해 줍니다.</div>
          </div>
          <div v-else class="p-code p-mt-8">
            <div class="p-code__title">
              <span v-if="invite.resent">동의 코드를 다시 보냈습니다.</span>
              <span v-else-if="fromPending">코드 대기 중인 요청이 있습니다.</span>
              <span v-else>강사 메일로 동의 코드를 보냈습니다.</span>
              강사가 전달한 코드를 입력하세요.
            </div>
            <div class="p-code__meta p-mono">
              만료 {{ fmtDateTime(invite.expires_at) }}<template v-if="invite.sent_count != null"> · 발송 {{ invite.sent_count }}회</template>
            </div>
            <div class="p-inline p-mt-8">
              <input
                v-model.trim="code"
                type="text"
                inputmode="numeric"
                maxlength="6"
                class="p-input p-input--mono p-code__input"
                placeholder="6자리 코드"
                @keyup.enter="confirm"
              />
              <a-btn kind="primary" :disabled="code.length !== 6 || confirming" @click="confirm">{{ confirming ? '확인 중...' : '코드 확인' }}</a-btn>
            </div>
            <div class="p-row-actions p-mt-8">
              <a-btn size="sm" kind="ghost" :disabled="sending || cooldown > 0" @click="sendInvite">{{ cooldown > 0 ? `재발송 (${cooldown}초)` : '코드 다시 보내기' }}</a-btn>
              <a-btn size="sm" kind="danger" :disabled="cancelling" @click="cancelInvite">요청 취소</a-btn>
            </div>
          </div>
        </template>
      </div>

      <div v-if="actionError" class="p-alert p-alert--err"><div class="p-alert__body">{{ actionError }}</div></div>
    </div>

    <template #foot>
      <a-btn kind="ghost" @click="$emit('close')">닫기</a-btn>
      <a-btn v-if="canInvite && !invite" kind="primary" :disabled="sending" @click="sendInvite">{{ sending ? '보내는 중...' : '동의 코드 보내기' }}</a-btn>
    </template>
  </a-modal>
</template>

<script>
import AModal from '@/components/Pineapple/AModal.vue';
import ABtn from '@/components/Pineapple/ABtn.vue';
import { fmtDateTime, voucherError, voucherErrorText } from '../orgUtils';

export default {
  name: 'InstructorAddModal',
  components: { AModal, ABtn },
  props: {
    orgIdx: { type: [Number, String], required: true },
  },
  emits: ['close', 'added', 'invited'],
  data() {
    return {
      email: '',
      result: null,       // 검색 결과 { users_idx, name(가림), email, is_instructor, already_member, pending_invite }
      notFound: false,
      searching: false,
      searchError: '',
      invite: null,       // { invite_idx, expires_at, sent_count, resent, max_attempts, status }
      fromPending: false, // 검색 결과의 pending_invite 로 이어 받은 요청인지
      code: '',
      sending: false,
      confirming: false,
      cancelling: false,
      cooldown: 0,
      cooldownTimer: null,
      actionError: '',
    };
  },
  computed: {
    canInvite() {
      const r = this.result;
      return !!(r && r.is_instructor !== false && !r.already_member);
    },
    displayName() {
      return (this.invite && this.invite.name) || (this.result && this.result.name) || '';
    },
  },
  beforeUnmount() {
    if (this.cooldownTimer) clearInterval(this.cooldownTimer);
  },
  methods: {
    fmtDateTime,
    reset() {
      this.result = null; this.notFound = false; this.searchError = ''; this.invite = null;
      this.fromPending = false; this.code = ''; this.actionError = '';
    },
    async search() {
      if (!this.email) return;
      this.searching = true;
      this.reset();
      try {
        const res = await this.$axios({
          method: 'get',
          url: `/voucher/org/${this.orgIdx}/instructors/search`,
          params: { email: this.email },
          loadingBar: false,
        });
        const d = res.data;
        const r = Array.isArray(d) ? d[0] : d;
        if (!r || (r.users_idx == null && !r.email)) { this.notFound = true; return; }
        this.result = r;
        // 대기 중 요청이 있으면 바로 코드 입력 단계로 (객체면 그대로, true 면 재발송으로 invite_idx 를 받는다)
        const p = r.pending_invite;
        if (p && typeof p === 'object' && p.invite_idx != null) {
          this.invite = { ...p, resent: false };
          this.fromPending = true;
        } else if (p === true) {
          this.fromPending = true;
          await this.sendInvite();
        }
      } catch (e) {
        const err = voucherError(e);
        if (err.status === 404 || err.code === 'USER_NOT_FOUND') this.notFound = true;
        else this.searchError = voucherErrorText(e, '검색하지 못했습니다.');
      } finally {
        this.searching = false;
      }
    },
    /** 동의 코드 발송(처음) 또는 재발송(대기 중 요청이 있으면 서버가 재발송으로 처리, resent=true) */
    async sendInvite() {
      if (!this.email || this.sending) return;
      this.sending = true;
      this.actionError = '';
      try {
        const res = await this.$axios({
          method: 'post',
          url: `/voucher/org/${this.orgIdx}/instructors/invites`,
          data: { email: this.email },
          loadingBar: true,
        });
        this.invite = res.data || {};
        this.code = '';
        this.startCooldown(60);
        this.$notifyVue({ type: 'success', title: this.invite.resent ? '동의 코드를 다시 보냈습니다.' : '강사 메일로 동의 코드를 보냈습니다.' });
        this.$emit('invited', this.invite);
      } catch (e) {
        const err = voucherError(e);
        if (err.code === 'ORG_INVITE_RATE_LIMITED' && err.detail && err.detail.retry_after) {
          this.startCooldown(Number(err.detail.retry_after) || 60);
        }
        this.actionError = voucherErrorText(e, '동의 코드를 보내지 못했습니다.');
      } finally {
        this.sending = false;
      }
    },
    async confirm() {
      if (!this.invite || this.code.length !== 6 || this.confirming) return;
      this.confirming = true;
      this.actionError = '';
      try {
        const res = await this.$axios({
          method: 'post',
          url: `/voucher/org/${this.orgIdx}/instructors/invites/${this.invite.invite_idx}/confirm`,
          data: { code: this.code },
          loadingBar: true,
        });
        const m = res.data || {};
        this.$notifyVue({ type: 'success', title: `"${m.name || this.email}" 강사를 기관에 등록했습니다.` });
        this.$emit('added', m);
      } catch (e) {
        const err = voucherError(e);
        if (err.code === 'ORG_INVITE_CODE_INVALID') {
          const left = err.detail && err.detail.attempts_left != null ? ` (남은 ${err.detail.attempts_left}회)` : '';
          this.actionError = `코드가 맞지 않습니다${left}.`;
        } else if (err.code === 'ORG_INVITE_EXPIRED') {
          this.actionError = '동의 코드가 만료됐습니다. ‘코드 다시 보내기’ 로 새 코드를 요청하세요.';
        } else if (err.code === 'ORG_INVITE_NOT_PENDING' || err.code === 'ORG_INVITE_NOT_FOUND') {
          this.actionError = voucherErrorText(e);
          this.invite = null;
        } else {
          this.actionError = voucherErrorText(e, '코드를 확인하지 못했습니다.');
        }
      } finally {
        this.confirming = false;
      }
    },
    async cancelInvite() {
      if (!this.invite || this.cancelling) return;
      this.cancelling = true;
      this.actionError = '';
      try {
        await this.$axios({
          method: 'post',
          url: `/voucher/org/${this.orgIdx}/instructors/invites/${this.invite.invite_idx}/cancel`,
          loadingBar: true,
        });
        this.$notifyVue({ type: 'success', title: '등록 요청을 취소했습니다.' });
        this.invite = null;
        this.fromPending = false;
        this.code = '';
        this.$emit('invited', null);
      } catch (e) {
        this.actionError = voucherErrorText(e, '요청을 취소하지 못했습니다.');
      } finally {
        this.cancelling = false;
      }
    },
    startCooldown(sec) {
      if (this.cooldownTimer) clearInterval(this.cooldownTimer);
      this.cooldown = sec;
      this.cooldownTimer = setInterval(() => {
        this.cooldown -= 1;
        if (this.cooldown <= 0) { clearInterval(this.cooldownTimer); this.cooldownTimer = null; this.cooldown = 0; }
      }, 1000);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../_org.scss';

.p-result {
  padding: 12px;
  border: 1px solid $p-line;
  border-radius: 10px;
  background: $p-panel-alt;
}
.p-code {
  padding: 12px 14px;
  border: 1px dashed $p-line-hard;
  border-radius: 10px;
  background: $p-point-soft;
  &__title { font-size: 12px; font-weight: 700; color: $p-ink; }
  &__meta { margin-top: 4px; color: $p-ink-mid; }
  &__input { max-width: 160px; font-size: 16px; letter-spacing: 0.2em; text-align: center; }
}
</style>
